<template>
  <div class="compact-uploader">
    <div
      class="drop-zone"
      :class="{ 'is-dragging': isDragging, 'has-file': previewUrl }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
      @click="triggerFileInput"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="accept"
        @change="handleFileSelect"
        style="display: none"
      />

      <div v-if="!previewUrl && !isUploading" class="prompt">
        <div class="icon">{{ icon }}</div>
        <div>{{ label }}</div>
      </div>

      <div v-if="isUploading" class="progress-indicator">
        <div>Uploading...</div>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
        </div>
      </div>

      <div v-if="previewUrl && !isUploading" class="file-preview">
        <img v-if="isImage" :src="previewUrl" alt="Preview" />
        <div v-else class="file-icon">📦</div>
        <button @click.stop="removeFile" class="remove-btn">✕</button>
        <div v-if="uploadComplete" class="pending-notice">Pending validation</div>
      </div>
    </div>
    <p v-if="validationError" class="validation-error">
      {{ validationError }}
    </p>
    <p v-else-if="resolutionWarning" class="resolution-warning">
      {{ resolutionWarning }}
    </p>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, type PropType } from 'vue';
import { getAcceptedUploadTypes, getAssetValidationError } from '@/utils/assetUpload';

export default defineComponent({
  name: 'CompactUploader',
  props: {
    fileType: {
      type: String as PropType<'logo' | 'map-marker' | 'ar-asset' | 'background'>,
      required: true,
    },
    label: {
      type: String,
      default: 'Upload File',
    },
    icon: {
      type: String,
      default: '📤',
    },
  },
  emits: ['upload-complete', 'remove', 'validation-error'],
  setup(props, { emit }) {
    const fileInput = ref<HTMLInputElement>();
    const isDragging = ref(false);
    const isUploading = ref(false);
    const uploadProgress = ref(0);
    const previewUrl = ref<string | null>(null); // local object URL for preview
    const uploadComplete = ref(false);
    const validationError = ref<string | null>(null);
    const resolutionWarning = ref<string | null>(null);
    const MIN_BACKGROUND_WIDTH = 1080;
    const MIN_BACKGROUND_HEIGHT = 1920;

    const accept = computed(() => getAcceptedUploadTypes(props.fileType));

    const isImage = computed(() => props.fileType !== 'ar-asset');

    // Bubble validation state up so the parent modal can hard-block its
    // Done/close button while an uploader has a rejected file in it.
    watch(validationError, (err) => {
      emit('validation-error', err);
    });

    const triggerFileInput = () => {
      fileInput.value?.click();
    };

    const handleFileSelect = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        uploadFile(target.files[0]);
      }
    };

    const handleDrop = (event: DragEvent) => {
      isDragging.value = false;
      if (event.dataTransfer?.files[0]) {
        uploadFile(event.dataTransfer.files[0]);
      }
    };

    const readImageDimensions = (src: string): Promise<{ width: number; height: number } | null> =>
      new Promise((resolve) => {
        const image = new Image();
        image.onload = () => {
          resolve({ width: image.naturalWidth, height: image.naturalHeight });
        };
        image.onerror = () => resolve(null);
        image.src = src;
      });

    const uploadFile = async (file: File) => {
      // Just stage the file locally, don't upload yet
      console.log('Staging file locally:', file.name);
      validationError.value = getAssetValidationError(file, props.fileType);
      resolutionWarning.value = null;

      if (validationError.value) {
        uploadComplete.value = false;
        if (previewUrl.value) {
          URL.revokeObjectURL(previewUrl.value);
        }
        previewUrl.value = null;
        if (fileInput.value) {
          fileInput.value.value = '';
        }
        return;
      }
      
      // prepare local preview ASAP
      if (isImage.value) {
        try {
          if (previewUrl.value) {
            URL.revokeObjectURL(previewUrl.value);
          }
          const url = URL.createObjectURL(file);
          previewUrl.value = url;
          if (props.fileType === 'background') {
            const dimensions = await readImageDimensions(url);
            if (
              dimensions &&
              (dimensions.width < MIN_BACKGROUND_WIDTH || dimensions.height < MIN_BACKGROUND_HEIGHT)
            ) {
              resolutionWarning.value = `This image is ${dimensions.width} x ${dimensions.height}. Full-screen backgrounds under ${MIN_BACKGROUND_WIDTH} x ${MIN_BACKGROUND_HEIGHT} may look blurry.`;
            }
          }
        } catch {}
      }

      // Emit the staged file info (no upload yet)
      emit('upload-complete', { 
        file: file,
        staged: true,
        localPreview: previewUrl.value
      });

      uploadComplete.value = true;
    };
    
    const removeFile = () => {
      if (previewUrl.value) {
        URL.revokeObjectURL(previewUrl.value);
      }
      previewUrl.value = null;
      uploadComplete.value = false;
      validationError.value = null;
      resolutionWarning.value = null;
      if (fileInput.value) {
        fileInput.value.value = '';
      }
      emit('remove');
    }

    return {
      fileInput,
      isDragging,
      isUploading,
      uploadProgress,
      previewUrl,
      accept,
      isImage,
      uploadComplete,
      validationError,
      resolutionWarning,
      triggerFileInput,
      handleFileSelect,
      handleDrop,
      removeFile
    };
  },
});
</script>

<style scoped>
.compact-uploader {
  width: 150px;
  min-height: 120px;
}
.drop-zone {
  width: 100%;
  height: 100%;
  border: 2px dashed var(--color-border);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;
}
.drop-zone.is-dragging,
.drop-zone:hover {
  border-color: var(--vt-c-accent);
  background-color: var(--color-background-soft);
}
.prompt {
  text-align: center;
  font-size: 0.8rem;
  color: var(--color-text);
}
.icon {
  font-size: 2rem;
  margin-bottom: 5px;
}
.progress-indicator {
  text-align: center;
}
.progress-bar {
  width: 80%;
  height: 5px;
  background-color: var(--color-border);
  border-radius: 3px;
  margin: 5px auto 0;
  overflow: hidden;
}
.progress-fill {
  width: 0;
  height: 100%;
  background-color: var(--vt-c-accent);
  transition: width 0.3s;
}
.file-preview {
  width: 100%;
  height: 100%;
  position: relative;
}
.file-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
}
.file-icon {
  font-size: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
.pending-notice {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 0.75rem;
  padding: 4px;
  text-align: center;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
}
.remove-btn {
    position: absolute;
    top: 5px;
    right: 5px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: none;
    background-color: rgba(0, 0, 0, 0.5);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
}

.resolution-warning {
  margin-top: 8px;
  font-size: 0.78rem;
  line-height: 1.4;
  color: #f59e0b;
  text-align: center;
}

.validation-error {
  margin-top: 8px;
  font-size: 0.78rem;
  line-height: 1.4;
  color: #ef4444;
  text-align: center;
}
</style>
